<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core';
import { computed, onMounted } from 'vue';

import { RealtimeRule } from '@/api/dynamic-yield/experiences';
import Breadcrumb from '@/components/base/layout/Breadcrumb.vue';
import Container from '@/components/base/layout/Container.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header2 from '@/components/base/typography/Header2.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import ProductCardWithOptions from '@/components/product-card/ProductCardWithOptions.vue';
import InformationBanner from '@/components/quiz/InformationBanner.vue';
import { useDyChooseResults } from '@/composables/dynamic-yield/experiences';
import { useCms } from '@/composables/useCms';
import { CouponResponse } from '@/data/quiz/onsiteOffer';
import { RecommendationsChoice } from '@/lib/personalization/dynamicYield';
import {
  formatAnalyticsMetadata,
  ListSourceAttribution,
} from '@/utils/analytics/listSourceAttribution';
import { CategoriesThumbnailGroup, DataModel } from '@/utils/cms';
import {
  analyticsMetadata,
  productSlotsFromChoice,
} from '@/utils/dynamic-yield/recommendationsRow';
import { ProductCard as ProductCardModel } from '@/utils/productCard';

const couponResponse = useLocalStorage<CouponResponse>('quiz.couponResponse', {});
const existingContact = useLocalStorage('quiz.existingContact', false);
const realtimeRules = useLocalStorage<RealtimeRule[]>('quiz.realtimeRules', []);
const selector = useLocalStorage('quiz.selector', '');

const { choicesByName, loadExperiences } = useDyChooseResults<RecommendationsChoice>(
  selector.value,
);
const choice = computed(() => choicesByName[selector.value]);
const recommendations = computed(() =>
  productSlotsFromChoice(choice.value).map(ProductCardModel.fromDY),
);

const listTitle = 'Your hand-picked snacks are ready - Let’s get shopping!';

const listSourceAttribution = computed<ListSourceAttribution | undefined>(() => {
  const metadata = analyticsMetadata(choice.value);
  if (!metadata) return undefined;
  return {
    ...formatAnalyticsMetadata(metadata),
    listSource: 'Dynamic Yield',
    listTitle,
  };
});

const { content: cmsContent } = useCms<DataModel<CategoriesThumbnailGroup>>(
  'categories-thumbnail-group',
  {
    loadOnMount: true,
  },
);

onMounted(async () => {
  await loadExperiences({
    realtimeRules: {
      [selector.value]: realtimeRules.value || [],
    },
  });
});
</script>

<template>
  <Container>
    <Breadcrumb :ancestors="[]" class="mt-4">
      <span aria-current="page" class="inline-block py-1 text-xs font-bold sm:text-sm">
        Quiz Results
      </span>
    </Breadcrumb>

    <InformationBanner :couponResponse :existingContact />
    <Header2 class="normal-case" headerTag="h1">{{ listTitle }}</Header2>
    <div class="my-4 md:my-6">
      <BaseBodyText class="text-neutral-500">{{ recommendations.length }} results</BaseBodyText>
      <div
        v-if="recommendations.length"
        class="grid grid-cols-2 md:pt-5 md:grid-cols-4 gap-x-4 md:gap-x-10 shrink-0 gap-y-10"
      >
        <div
          v-for="(productCard, index) in recommendations"
          :key="productCard.sku"
          class="flex flex-col w-full"
        >
          <ProductCardWithOptions
            :decisionId="choice?.decisionId"
            layout="plp"
            :listSourceAttribution
            pageSection="Product Recommendations"
            :position="index"
            :product="productCard"
            :showVariants="false"
            :slotId="productCard.slotId"
            trackingEventLocation="quizResults"
          />
        </div>
      </div>
    </div>
    <div v-if="cmsContent.result?.data?.categories.length" class="mb-6">
      <Header4 class="font-gelica">{{ cmsContent.result.data.listTitle }}</Header4>
      <div class="flex gap-2 mt-4 overflow-scroll md:gap-x-4">
        <div
          v-for="category in cmsContent.result.data.categories"
          :key="category.name"
          class="min-w-24 max-w-36 lg:max-w-64"
        >
          <RouterLink v-if="category.url" :to="category.url" class="no-underline color-inherit">
            <img :src="category.image" :alt="category.name" />
            <BaseBodyText class="mt-2 font-semibold text-center">{{ category.name }}</BaseBodyText>
          </RouterLink>
        </div>
      </div>
    </div>
  </Container>
</template>
